import axios from 'axios';
import { API_CONFIG } from '../config/api';
import WebApp from '@twa-dev/sdk';
import { getCurrentEnvironment } from '../utils/environment';

// API Error handling class
class ApiError extends Error {
  constructor(message: string, public status: number) {
    super(message);
    this.name = 'ApiError';
  }
}

// Public endpoints that don't require authentication
const PUBLIC_ENDPOINTS = ['/user/signup', '/user/auth', '/user/reset-password/request', '/user/reset-password/confirm'];

// Create axios instance
const api = axios.create({
  baseURL: API_CONFIG.BASE_URL,
  headers: API_CONFIG.HEADERS,
  withCredentials: true // Enable sending cookies in cross-origin requests
});

// Add request interceptor for setting authorization header and logging
api.interceptors.request.use((config) => {
  // Get current environment
  const environment = getCurrentEnvironment();
  
  // Check if the endpoint is public (doesn't require authentication)
  const isPublicEndpoint = PUBLIC_ENDPOINTS.some(endpoint => 
    config.url?.endsWith(endpoint)
  );
  
  // If it's a public endpoint, no need for authentication
  if (isPublicEndpoint) {
    // Log request
    // console.log(`API Request (public): ${config.method?.toUpperCase()} ${config.baseURL}${config.url}`, {
    //   params: config.params,
    //   data: config.data
    // });
    return config;
  }
  
  // Handle authorization based on environment
  if (environment === 'telegram') {
    try {
      // Telegram Mini App authentication
      const initData = WebApp.initData;
      
      if (initData) {
        // Set authorization header for Telegram
        config.headers.Authorization = `tma ${initData}`;
      } else {
        console.warn('WebApp.initData is missing, request may fail');
      }
    } catch (error) {
      console.error('Error accessing WebApp.initData:', error);
    }
  } else if (environment === 'pwa' || environment === 'browser') {
    // JWT authentication for PWA/browser
    const token = localStorage.getItem('authToken');
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      // For PWA/browser, we need a token for non-public endpoints
      console.warn('Auth token is missing for authenticated endpoint');
      
      // Don't redirect immediately for missing tokens - let the response interceptor handle it
      // This prevents race conditions when tokens are cleared during refresh failures
      throw new ApiError('Authentication token is missing', 401);
    }
  }
  
  // Log request with more details
  // console.log(`API Request: ${config.method?.toUpperCase()} ${config.baseURL}${config.url}`, {
  //   params: config.params,
  //   data: config.data,
  //   headers: {
  //     'Content-Type': config.headers['Content-Type'],
  //     'Accept': config.headers['Accept'],
  //     'Authorization': config.headers['Authorization'] ? 
  //       (typeof config.headers['Authorization'] === 'string' && config.headers['Authorization'].startsWith('tma ') ? 'tma ***' : 'Bearer ***') : 'Not set'
  //   },
  //   environment,
  //   fullHeaders: config.headers
  // });
  
  return config;
});

// Centralized authentication error handling
api.interceptors.response.use(
  response => {
    // Log successful response
    // console.log(`API Response: ${response.status} ${response.config.method?.toUpperCase()} ${response.config.url}`, {
    //   data: response.data
    // });
    return response;
  },
  async error => {
    // Log error response
    console.error(`API Error: ${error.config?.method?.toUpperCase()} ${error.config?.url}`, {
      status: error.response?.status,
      data: error.response?.data,
      message: error.message
    });
    
    // Centralized authentication error handling
    const environment = getCurrentEnvironment();
    const isPWAOrBrowser = environment === 'pwa' || environment === 'browser';
    const isAuthEndpoint = error.config.url?.includes('/user/auth') || 
                          error.config.url?.includes('/user/signup') ||
                          error.config.url?.includes('/user/reset-password');
    
    // Handle 401 Unauthorized errors (token expired or invalid)
    if (error.response && error.response.status === 401) {
      if (isPWAOrBrowser && !isAuthEndpoint) {
        // Check if this is a "missing token" error vs "expired token" error
        const hasToken = localStorage.getItem('authToken');
        
        if (!hasToken) {
          // No token present - redirect to login
          // console.log('No auth token present, redirecting to login');
          handleAuthenticationFailure();
          throw new ApiError('Authentication required. Please log in.', 401);
        }
        
        // Skip refresh for auth endpoints to avoid infinite loops
        if (!error.config.url?.includes('/user/refresh')) {
          try {
            // Try to refresh the token
            const response = await api.post('/user/refresh', {}, {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('authToken')}`
              }
            });
            
            // Store the new token
            const newToken = response.data.token;
            localStorage.setItem('authToken', newToken);
            
            // Retry the original request with the new token
            const originalRequest = error.config;
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return api(originalRequest);
          } catch (refreshError) {
            // If refresh token fails, handle authentication failure centrally
            console.error('Token refresh failed:', refreshError);
            handleAuthenticationFailure();
            throw new ApiError('Session expired. Please log in again.', 401);
          }
        } else {
          // Refresh token itself failed, handle authentication failure
          console.error('Refresh token failed:', error);
          handleAuthenticationFailure();
          throw new ApiError('Session expired. Please log in again.', 401);
        }
      }
    }
    
    // Handle 403 Forbidden errors (insufficient permissions)
    if (error.response && error.response.status === 403) {
      if (isPWAOrBrowser && !isAuthEndpoint) {
        console.error('Access forbidden:', error);
        handleAuthenticationFailure();
        throw new ApiError('Access denied. Please log in again.', 403);
      }
    }
    
    // Handle network errors that might be CORS-related
    if (error.message && error.message.includes('Network Error')) {
      console.warn('Network error detected (likely CORS):', error);
      
      // Check if this might be an authentication issue
      // If we have a token but get CORS errors, it could be an auth problem
      const hasToken = localStorage.getItem('authToken');
      if (hasToken && isPWAOrBrowser && !isAuthEndpoint) {
        // console.log('CORS error with token present, treating as potential auth issue');
        handleAuthenticationFailure();
        throw new ApiError('Authentication failed. Please log in again.', 401);
      }
      
      // For other network errors, just re-throw
      throw new ApiError('Network error. Please check your connection.', 500);
    }
    
    if (error.response) {
      throw new ApiError(
        error.response.data.error || 'Unknown error',
        error.response.status
      );
    }
    throw new ApiError(error.message || 'Network error', 500);
  }
);

// Centralized function to handle authentication failures
const handleAuthenticationFailure = () => {
  // Clear authentication state
  localStorage.removeItem('authToken');
  
  // Force a storage event to trigger auth state update in App.tsx
  window.dispatchEvent(new StorageEvent('storage', {
    key: 'authToken',
    newValue: null,
    storageArea: localStorage
  }));
  
  // Redirect to login page if not already there
  if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
    // console.log('Redirecting to login due to authentication failure');
    window.location.href = '/login';
  }
};

export default api; 